import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import Pagination from 'react-reactstrap-pagination';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Table,
} from 'reactstrap';

import Loader from '~/components/Loader/Loader';
import { api2 } from '~/services/api';

import CommunityDeleteModal from './CommunityDeleteModal';
import CommunityDetailsModal from './CommunityDetailsModal';
import CommunityFormModal from './CommunityFormModal';
import handleCommunityError from './communityError';
import {
  buildCommunityFilterSearch,
  buildCommunityListParams,
  COMMUNITY_LIMIT,
  COMMUNITY_TYPES,
  getCommunityErrorMessage,
  getCommunityTypeLabel,
  isCommunityAdmin,
  readCommunityFilters,
} from './communityUtils';
import './styles.css';

async function loadRaceCalendarNames(raceCalendarIds, isAdmin) {
  const pendingIds = new Set(raceCalendarIds.filter(Boolean));
  const names = {};
  const limit = 100;
  let skip = 0;
  let total = pendingIds.size ? 1 : 0;

  while (pendingIds.size > 0 && skip < total) {
    const response = await api2.get(
      isAdmin ? '/race_calendar' : '/race_calendar/all',
      { params: { skip, limit } }
    );
    const responseData = response.data || {};
    const races = Array.isArray(responseData.data) ? responseData.data : [];

    races.forEach(race => {
      if (pendingIds.has(race._id)) {
        names[race._id] = race.name;
        pendingIds.delete(race._id);
      }
    });

    total = Number(responseData.total) || 0;
    skip += limit;

    if (races.length === 0) break;
  }

  return names;
}

export default function Communities() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const profile = useSelector(state => state.user.profile);
  const initialFilters = useRef(readCommunityFilters(location.search)).current;
  const firstSearchRender = useRef(true);

  const [communities, setCommunities] = useState([]);
  const [raceCalendarNames, setRaceCalendarNames] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialFilters.page);
  const [name, setName] = useState(initialFilters.name);
  const [nameInput, setNameInput] = useState(initialFilters.name);
  const [type, setType] = useState(initialFilters.type);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [formCommunity, setFormCommunity] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [detailsId, setDetailsId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = isCommunityAdmin(profile);

  const loadCommunities = useCallback(async () => {
    setLoading(true);
    setLoadError('');

    try {
      const response = await api2.get('/communities', {
        params: buildCommunityListParams({ name, type, page }),
      });
      const responseData = response.data || {};

      setCommunities(
        Array.isArray(responseData.data) ? responseData.data : []
      );
      setTotal(Number(responseData.total) || 0);

      const loadedCommunities = Array.isArray(responseData.data)
        ? responseData.data
        : [];
      try {
        const raceNames = await loadRaceCalendarNames(
          loadedCommunities.map(community => community.race_calendar_id),
          isAdmin
        );
        setRaceCalendarNames(raceNames);
      } catch (raceCalendarError) {
        setRaceCalendarNames({});
        handleCommunityError(
          raceCalendarError,
          dispatch,
          'Não foi possível carregar os nomes das corridas vinculadas.'
        );
      }
    } catch (error) {
      const message = getCommunityErrorMessage(
        error,
        'Não foi possível carregar as comunidades.'
      );
      setLoadError(message);
      handleCommunityError(
        error,
        dispatch,
        'Não foi possível carregar as comunidades.'
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, isAdmin, name, page, type]);

  useEffect(() => {
    loadCommunities();
  }, [loadCommunities]);

  useEffect(() => {
    const search = buildCommunityFilterSearch({ name, type, page });
    if (search !== location.search) {
      history.replace({ pathname: location.pathname, search });
    }
  }, [history, location.pathname, location.search, name, page, type]);

  useEffect(() => {
    if (firstSearchRender.current) {
      firstSearchRender.current = false;
      return undefined;
    }

    const timeout = setTimeout(() => {
      setName(nameInput.trim());
      setPage(1);
    }, 500);

    return () => clearTimeout(timeout);
  }, [nameInput]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setFormCommunity(null);
  }, []);

  const closeDetails = useCallback(() => setDetailsId(null), []);
  const closeDelete = useCallback(() => setDeleteTarget(null), []);

  function openCreate() {
    setFormCommunity(null);
    setFormOpen(true);
  }

  function openEdit(community) {
    setFormCommunity(community);
    setFormOpen(true);
  }

  function handleTypeChange(event) {
    setType(event.target.value);
    setPage(1);
  }

  async function handleDelete() {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await api2.delete(`/communities/${deleteTarget.id}`);
      toast.success('Comunidade excluída com sucesso.');
      closeDelete();

      if (communities.length === 1 && page > 1) {
        setPage(currentPage => currentPage - 1);
      } else {
        loadCommunities();
      }
    } catch (error) {
      handleCommunityError(
        error,
        dispatch,
        'Não foi possível excluir a comunidade.'
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      {formOpen && (
        <CommunityFormModal
          community={formCommunity}
          raceCalendarName={
            formCommunity &&
            raceCalendarNames[formCommunity.race_calendar_id]
          }
          open={formOpen}
          toggle={closeForm}
          onSaved={loadCommunities}
        />
      )}

      {detailsId && (
        <CommunityDetailsModal
          communityId={detailsId}
          raceCalendarName={
            raceCalendarNames[
              communities.find(community => community.id === detailsId)
                ?.race_calendar_id
            ]
          }
          open={Boolean(detailsId)}
          toggle={closeDetails}
        />
      )}

      {deleteTarget && (
        <CommunityDeleteModal
          community={deleteTarget}
          deleting={deleting}
          open={Boolean(deleteTarget)}
          toggle={closeDelete}
          onConfirm={handleDelete}
        />
      )}

      <Row>
        <Col>
          <div className="page-title-box">
            <Row>
              <Col lg={7}>
                <h4 className="page-title">Comunidades</h4>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              {loading && <Loader />}

              <div className="communities-toolbar">
                <div className="communities-summary">
                  {isAdmin && (
                    <Button color="primary" onClick={openCreate}>
                      Cadastrar comunidade
                    </Button>
                  )}
                  <span>Total ({total})</span>
                </div>

                <div className="communities-filters">
                  <div>
                    <label htmlFor="community-search">Nome</label>
                    <Input
                      id="community-search"
                      value={nameInput}
                      onChange={event => setNameInput(event.target.value)}
                      placeholder="Buscar por nome"
                    />
                  </div>
                  <div>
                    <label htmlFor="community-filter-type">Tipo</label>
                    <Input
                      id="community-filter-type"
                      type="select"
                      value={type}
                      onChange={handleTypeChange}
                    >
                      <option value="">Todos</option>
                      {COMMUNITY_TYPES.map(communityType => (
                        <option
                          key={communityType.value}
                          value={communityType.value}
                        >
                          {communityType.label}
                        </option>
                      ))}
                    </Input>
                  </div>
                </div>
              </div>

              {loadError && (
                <Alert color="danger">
                  {loadError}{' '}
                  <Button color="link" onClick={loadCommunities}>
                    Tentar novamente
                  </Button>
                </Alert>
              )}

              <div className="communities-table-wrapper">
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Imagem</th>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>Ordem</th>
                      <th>Cidade/UF</th>
                      <th>Corrida vinculada</th>
                      <th>Criação</th>
                      <th>URL</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && communities.length === 0 && (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          Nenhuma comunidade encontrada.
                        </td>
                      </tr>
                    )}
                    {communities.map(community => (
                      <tr key={community.id}>
                        <td>{community.id}</td>
                        <td>
                          {community.image ? (
                            <a
                              href={community.image}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Abrir imagem em uma nova aba"
                            >
                              <img
                                className="community-table-image"
                                src={community.image}
                                alt={`Imagem da comunidade ${community.name}`}
                              />
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <Button
                            color="link"
                            className="community-name-button"
                            onClick={() => setDetailsId(community.id)}
                          >
                            {community.name}
                          </Button>
                        </td>
                        <td>{getCommunityTypeLabel(community.type)}</td>
                        <td>{community.order ?? 0}</td>
                        <td>
                          {community.city} - {community.state}
                        </td>
                        <td>
                          {community.race_calendar_id
                            ? raceCalendarNames[community.race_calendar_id] ||
                              'Corrida não encontrada'
                            : '-'}
                        </td>
                        <td>
                          {community.created_at
                            ? moment(community.created_at).format('DD/MM/YYYY')
                            : '-'}
                        </td>
                        <td>
                          <a
                            href={community.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Abrir
                          </a>
                        </td>
                        <td className="communities-actions">
                          <Button
                            color="info"
                            size="sm"
                            onClick={() => setDetailsId(community.id)}
                          >
                            Visualizar
                          </Button>
                          {isAdmin && (
                            <>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => openEdit(community)}
                              >
                                Editar
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => setDeleteTarget(community)}
                              >
                                Excluir
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {total > COMMUNITY_LIMIT && (
        <Row>
          <Col xs={12} className="d-flex justify-content-center">
            <Pagination
              firstPageText="Início"
              lastPageText="Fim"
              previousPageText="Anterior"
              nextPageText="Próximo"
              totalItems={total}
              pageSize={COMMUNITY_LIMIT}
              defaultActivePage={page}
              onSelect={setPage}
            />
          </Col>
        </Row>
      )}
    </>
  );
}
